<div class="flex flex-col gap-6">
  <!-- Clickable Equation Display -->
  <div
    class="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-neutral-200 py-4 transition hover:border-sky-500 hover:bg-neutral-50"
    (click)="openModal()"
  >
    <dojo-markdown-viewer class="mt-4" [content]="'$$' + getEquationLatex() + '$$'" />
  </div>

  @if (!state.isValid) {
    <div class="flex justify-center">
      <p class="text-sm text-red-500">Error: The coefficient 'a' cannot be zero</p>
    </div>
  }

  @if (state.isValid) {
    <!-- Step 1: Determine number of solutions -->
    <div class="flex flex-col gap-4">
      <div class="flex items-center gap-3">
        <div
          class="text-neutral-0 flex h-8 w-8 items-center justify-center rounded-full bg-sky-500 text-sm font-semibold"
        >
          1
        </div>
        <h3 class="text-lg font-medium leading-7 text-neutral-600">Determine the number of possible solutions</h3>
      </div>

      <div class="bg-neutral-0 rounded-2xl border border-neutral-200 p-6">
        <!-- Navigation Arrows -->
        <div class="mb-4 flex justify-end gap-2">
          <button
            (click)="prevSlide()"
            [disabled]="currentSlide() === 1"
            [class.text-neutral-400]="currentSlide() === 1"
            [class.text-sky-500]="currentSlide() > 1"
            class="flex h-10 w-10 items-center justify-center rounded-lg bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12.5 15L7.5 10L12.5 5"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <button
            (click)="nextSlide()"
            [disabled]="currentSlide() === 3"
            [class.text-neutral-400]="currentSlide() === 3"
            [class.text-sky-500]="currentSlide() < 3"
            class="flex h-10 w-10 items-center justify-center rounded-lg bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M7.5 15L12.5 10L7.5 5"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>

        @if (currentSlide() === 1) {
          <div class="flex flex-col gap-6">
            <!-- Discriminant Formula -->
            <dojo-markdown-viewer [content]="'$$\\Delta = b^2 - 4ac$$'" />

            <!-- Calculation with result -->
            <dojo-markdown-viewer [content]="state.discriminantLatex" />

            <!-- Conclusion -->
            <div class="flex items-start gap-2 text-base leading-6 text-neutral-900">
              <span>→</span>
              <span
                >The quadratic function has
                <span class="font-semibold"
                  >{{ state.hasRealRoots ? 'two distinct' : state.hasSingleRoot ? 'one' : 'no' }}&nbsp;</span
                >
                <span class="underline decoration-sky-500">real</span>
                {{
                  state.hasRealRoots || state.hasSingleRoot ? 'root' + (state.hasRealRoots ? 's' : '') : 'roots'
                }}</span
              >
            </div>

            <!-- Page Indicator -->
            <div class="mt-4 flex justify-end text-sm text-neutral-400">1/3</div>
          </div>
        }

        @if (currentSlide() === 2) {
          <div class="flex flex-col gap-6">
            <!-- Instruction Text -->
            <p class="text-base leading-6 text-neutral-600">
              Identify the coefficients of the quadratic equation, then substitute them into the discriminant formula.
            </p>

            <!-- Discriminant Formula -->
            <dojo-markdown-viewer [content]="'$$\\Delta = b^2 - 4ac$$'" />

            <!-- Equation with color-coded annotations and connections -->
            <dojo-super-latex [content]="state.step2SuperLatexContent">
              <!-- Labels as HTML positioned below equation -->
              <div class="flex justify-center gap-24">
                <span data-anchor="label-a" class="text-lg font-semibold text-sky-500">= a</span>
                <span data-anchor="label-b" class="text-lg font-semibold text-red-500">= b</span>
                <span data-anchor="label-c" class="text-lg font-semibold text-orange-500">= c</span>
              </div>
            </dojo-super-latex>

            <!-- Substitution -->
            <dojo-markdown-viewer [content]="state.step2SubstitutionLatex" />

            <!-- Page Indicator -->
            <div class="mt-4 flex justify-end text-sm text-neutral-400">2/3</div>
          </div>
        }

        @if (currentSlide() === 3) {
          <div class="flex flex-col gap-6">
            <!-- Discriminant Formula -->
            <dojo-markdown-viewer [content]="'$$\\Delta = b^2 - 4ac$$'" />

            <!-- Final calculation with comparison -->
            <dojo-markdown-viewer [content]="state.discriminantLatex" />

            <!-- Conclusion -->
            <div class="flex items-start gap-2 text-base leading-6 text-neutral-900">
              <span>→</span>
              <span
                >The quadratic function has
                <span class="font-semibold"
                  >{{ state.hasRealRoots ? 'two distinct' : state.hasSingleRoot ? 'one' : 'no' }}&nbsp;</span
                >
                <span class="underline decoration-sky-500">real</span>
                {{
                  state.hasRealRoots || state.hasSingleRoot ? 'root' + (state.hasRealRoots ? 's' : '') : 'roots'
                }}</span
              >
            </div>

            <!-- Page Indicator -->
            <div class="mt-4 flex justify-end text-sm text-neutral-400">3/3</div>
          </div>
        }
      </div>
    </div>

    <!-- Step 2: Solve using quadratic formula -->
    @if (state.hasRealRoots || state.hasSingleRoot) {
      <div class="flex flex-col gap-4">
        <div class="flex items-center gap-3">
          <div
            class="text-neutral-0 flex h-8 w-8 items-center justify-center rounded-full bg-sky-500 text-sm font-semibold"
          >
            2
          </div>
          <h3 class="text-lg font-medium leading-7 text-neutral-600">
            Use the quadratic formula to solve for the solutions
          </h3>
        </div>

        <div class="bg-neutral-0 relative rounded-2xl border border-neutral-200 p-6">
          <!-- Navigation Arrows -->
          <div class="absolute right-4 top-4 flex items-center gap-2">
            <button class="flex h-10 w-10 items-center justify-center rounded-lg bg-neutral-100 text-neutral-400">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M12.5 15L7.5 10L12.5 5"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            <button class="flex h-10 w-10 items-center justify-center rounded-lg bg-neutral-100 text-sky-500">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M7.5 15L12.5 10L7.5 5"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>

          <div class="flex flex-col gap-4">
            <!-- Quadratic Formula -->
            <dojo-markdown-viewer [content]="'$$x_{1,2} = \\frac{-b \\pm \\sqrt{\\Delta}}{2a}$$'" />

            <!-- Substitution -->
            <dojo-markdown-viewer [content]="state.substitutionLatex" />

            <!-- Conclusion -->
            <div class="flex items-start gap-2 text-base leading-6 text-neutral-900">
              <span>→</span>
              <span
                >Roots at
                <dojo-markdown-viewer
                  class="inline-block [&_markdown]:my-0 [&_markdown_p]:my-0"
                  [content]="'$x_1 = ' + state.x1 + '$'"
                  [applyProseClass]="false" />
                and
                <dojo-markdown-viewer
                  class="inline-block [&_markdown]:my-0 [&_markdown_p]:my-0"
                  [content]="'$x_2 = ' + state.x2 + '$'"
                  [applyProseClass]="false"
              /></span>
            </div>
          </div>

          <!-- Page Indicator -->
          <div class="absolute bottom-4 right-4 text-sm text-neutral-400">1/3</div>
        </div>
      </div>
    }
  }
</div>

<!-- Settings Modal -->
<ion-modal [isOpen]="isModalOpen()" [backdropDismiss]="true" (didDismiss)="closeModal()">
  <ng-template>
    <ion-header>
      <ion-toolbar>
        <ion-title>Edit Equation</ion-title>
      </ion-toolbar>
    </ion-header>

    <ion-content>
      <div class="flex flex-col gap-6 p-6">
        <!-- Coefficient a -->
        <y42-number-input
          label="Coefficient a"
          [formCtrl]="aCtrl"
          [errorMessages]="{ nonZero: 'Coefficient a cannot be zero' }"
        />

        <!-- Coefficient b -->
        <y42-number-input label="Coefficient b" [formCtrl]="bCtrl" />

        <!-- Coefficient c -->
        <y42-number-input label="Coefficient c" [formCtrl]="cCtrl" />

        <!-- Rounding -->
        <y42-number-input label="Decimal places" [formCtrl]="roundingCtrl" [min]="0" [max]="10" />
      </div>
    </ion-content>

    <ion-footer class="border-t border-neutral-200">
      <div class="flex justify-end gap-2 p-4">
        <dojo-button [fill]="'outline'" (click)="closeModal()">Done</dojo-button>
      </div>
    </ion-footer>
  </ng-template>
</ion-modal>
